import { createNode, isShapeValue } from '@glueapp/graph-types';
import type { DataValue } from '@glueapp/graph-types';

export const Webhook = createNode({
    id: 'Webhook',
    name: 'Webhook',
    description: '',
    aiDescription: [
        'Receives incoming HTTP webhook requests. Setup steps:',
        '1. Add the node: glue add Webhook',
        '2. (Optional) Type the headers — set "Expected Headers" to a shape value whose object properties are the header names you expect.',
        '   Header values are always strings. Example for Content-Type and X-Request-Id:',
        '   glue set-value <id> "Expected Headers" \'{"isShapeValue":true,"type":{"kind":"object","properties":{"Content-Type":{"kind":"string"},"X-Request-Id":{"kind":"string"}},"restriction":"open"}}\'',
        '   This retypes the Headers output port so downstream nodes see named string fields instead of an empty object.',
        '3. If the webhook sends a JSON body, enable it:',
        '   glue set-value <id> "Expect Body" true',
        '   - When false (default): the "Expected Body" input port and "Body" output port are HIDDEN.',
        '   - When true: both appear. The Body output defaults to an open object.',
        '4. (Optional) Type the body — set "Expected Body" to a shape value:',
        '   glue set-value <id> "Expected Body" \'{"isShapeValue":true,"type":{"kind":"object","properties":{"name":{"kind":"string"},"count":{"kind":"number"}},"restriction":"open"}}\'',
        '   This retypes the Body output port to match.',
        '5. Use glue shape <id> to verify the resulting ports after each value change.',
        '',
        'Output ports:',
        '- "Headers List": array of {name: string, value: string} — always present',
        '- "Headers": object typed by Expected Headers shape (or empty object) — always present',
        '- "Body": typed by Expected Body shape — only present when "Expect Body" is true',
    ].join('\n'),
    category: 'Trigger',
    isAsync: false,
    color: '#808080',
    inPorts: {
        'Expected Headers': {
            kind: 'shape',
            restriction: { kind: 'object', properties: {}, restriction: 'open' },
        },
        'Expect Body': {
            kind: 'boolean',
        },
        'Expected Body': {
            kind: 'shape',
            restriction: { kind: 'object', properties: {}, restriction: 'open' },
        },
    },
    outPorts: {
        'Headers List': {
            kind: 'array',
            subType: {
                kind: 'object',
                properties: {
                    name: { kind: 'string' },
                    value: { kind: 'string' },
                },
                restriction: 'exact',
            },
        },
        Headers: { kind: 'object', properties: {}, restriction: 'exact' },
        Body: {
            kind: 'string',
        },
    },
    computeShape: input => {
        const headerShapeValue = input.portValues['Expected Headers'];
        const expectBody = input.portValues['Expect Body'];
        const bodyShapeValue = input.portValues['Expected Body'];

        // Conditionally include/exclude the Expected Body input
        const inPorts = expectBody ? input.nodeInPorts : input.nodeInPorts.filter(port => port.name !== 'Expected Body');

        // Build output ports — remove Body when Expect Body is off
        let outPorts = expectBody ? input.nodeOutPorts : input.nodeOutPorts.filter(port => port.name !== 'Body');

        // Retype Headers output based on Expected Headers shape
        if (isShapeValue(headerShapeValue) && headerShapeValue.type.kind === 'object') {
            const headerProperties: Record<string, { kind: 'string' }> = {};
            for (const key of Object.keys(headerShapeValue.type.properties)) {
                headerProperties[key] = { kind: 'string' };
            }
            outPorts = outPorts.map(outPort => {
                if (outPort.name === 'Headers') {
                    return {
                        name: 'Headers',
                        type: { kind: 'object', properties: headerProperties, restriction: 'exact' },
                    };
                }
                return outPort;
            });
        }

        // Retype Body output based on Expected Body shape
        if (expectBody) {
            const bodyType = isShapeValue(bodyShapeValue)
                ? bodyShapeValue.type
                : { kind: 'object' as const, properties: {}, restriction: 'open' as const };
            outPorts = outPorts.map(outPort => {
                if (outPort.name === 'Body') {
                    return { name: 'Body', type: bodyType };
                }
                return outPort;
            });
        }

        return { inPorts, outPorts };
    },
    triggerType: 'Webhook',
    triggerEventIdentifier: 'webhookReceived',
    hideInherentInPort: true,
    execute: function (input) {
        // TODO Fix webhooks having no integration and therefore payload not being set here
        // @ts-expect-error
        const payload = input.payload as { headers: { name: string; value: string }[]; body: string };
        const headersArray = payload.headers ?? [];

        const headersObject: Record<string, string> = {};
        const shapeValue = input.inPorts['Expected Headers'];
        if (isShapeValue(shapeValue) && shapeValue.type.kind === 'object') {
            for (const headerName of Object.keys(shapeValue.type.properties)) {
                const found = headersArray.find(header => header.name.toLowerCase() === headerName.toLowerCase());
                headersObject[headerName] = found?.value ?? '';
            }
        }

        // Parse body if Expected Body shape is defined
        let bodyOutput: DataValue = (payload.body as DataValue) ?? '{}';
        const expectBody = input.inPorts['Expect Body'];
        if (expectBody) {
            try {
                bodyOutput = typeof payload.body === 'string' ? JSON.parse(payload.body) : payload.body;
            } catch {
                bodyOutput = payload.body ?? '{}';
            }
        }

        return {
            output: {
                'Headers List': headersArray,
                Headers: headersObject,
                Body: bodyOutput,
            },
        };
    },
});
